import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import appRoutes from '@/constants/routes/app';
import useAdminContentDetail from '@/hooks/content/useAdminContentDetail';
import useRemoveContent from '@/hooks/content/useRemoveContent';
import useRestoreContent from '@/hooks/content/useRestoreContent';
import { ContentStatusBadge } from '@/modules/content/common/contentDisplay';
import ContentView from '@/modules/content/common/ContentView';
import ModerationInfoPanel from '@/modules/content/moderation/ModerationInfoPanel';
import RemoveContentModal from '@/modules/content/moderation/RemoveContentModal';
import colors from '@/theme/colors';
import { Box, Center, Flex, HStack, useDisclosure } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React from 'react';

const ContentModerationDetail = () => {
  const { contentId } = Route.useParams();
  const navigate = useNavigate();

  const removeDisclosure = useDisclosure();
  const restoreDisclosure = useDisclosure();

  const { isLoading, isPending, isRefetching, isSuccess, data } = useAdminContentDetail(contentId);
  const { isPending: isRemoving, mutate: remove } = useRemoveContent(contentId, removeDisclosure.onClose);
  const { isPending: isRestoring, mutate: restore } = useRestoreContent(contentId, restoreDisclosure.onClose);

  const showLoader = isLoading || isPending || isRefetching;
  // Restore is an undo for admin takedowns only. Content the author withdrew
  // themselves is retained for review but must not be put back by an admin, so
  // only 'removed' offers Restore (the backend rejects the call either way).
  const isRemoved = data?.status === 'removed';

  const headerActions = data
    ? isRemoved
      ? (
          <CustomButton
            label="Restore"
            backgroundColor="extra.teal"
            labelColor="extra.white"
            labelHoverColor="extra.white"
            onClick={restoreDisclosure.onOpen}
          />
        )
      : (
          <CustomButton
            label="Remove"
            backgroundColor="extra.red"
            labelColor="extra.white"
            labelHoverColor="extra.white"
            onClick={removeDisclosure.onOpen}
          />
        )
    : undefined;

  return (
    <SidebarItemViewTemplate title="Content Detail" customButton={headerActions}>
      <HStack justify="space-between" width="100%" marginTop="20px">
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIconPaths.leftArrow}
          labelColor={colors.background.blueGray}
          labelHoverColor="extra.black"
          label="Back to Content Moderation"
          hoverBackgroundColor="transparent"
          borderHoverColor="transparent"
          onClick={() => navigate({ to: appRoutes.contentModeration })}
          labelStyle={{ marginBottom: '5px', fontSize: '16px' }}
        />
        {data && <ContentStatusBadge status={data.status} />}
      </HStack>

      {showLoader && (
        <Center width="100%" paddingY="40px">
          <LoadingLogo />
        </Center>
      )}

      {!showLoader && isSuccess && data && (
        <Flex align="flex-start" width="100%" gap="32px" marginTop="10px" marginBottom="40px" wrap="wrap">
          {/* Content, as readers see it */}
          <Box flex="1 1 480px" minWidth="0">
            <ContentView content={data} />
          </Box>

          {/* Moderation metadata + audit — a persistent side panel. */}
          <Box
            width="360px"
            flexShrink={0}
            alignSelf="flex-start"
            position="sticky"
            top="0"
            maxHeight="calc(100vh - 120px)"
            overflowY="auto"
            border="1px solid"
            borderColor="background.separatorBlue"
            borderRadius="12px"
            backgroundColor="white"
            padding="20px"
          >
            <ModerationInfoPanel content={data} />
          </Box>
        </Flex>
      )}

      <RemoveContentModal
        isOpen={removeDisclosure.isOpen}
        isLoading={isRemoving}
        onCancel={removeDisclosure.onClose}
        onConfirm={(removalReason) => remove(removalReason)}
      />

      <ConfirmationModal
        isOpen={restoreDisclosure.isOpen}
        title="Restore Content"
        description="This restores the content to its previous status (it becomes visible in the feed again if it was published)."
        confirmButtonText="Restore"
        confirmButtonColor="extra.teal"
        confirmLoading={isRestoring}
        onCancel={restoreDisclosure.onClose}
        onConfirm={() => restore()}
      />
    </SidebarItemViewTemplate>
  );
};

export default ContentModerationDetail;

export const Route = createFileRoute('/_authenticated/_admin/content/moderation/$contentId')({
  component: ContentModerationDetail,
});
